import React, { useState, useEffect, useContext } from "react";
import {
  Button,
  IconButton,
  Typography,
  TextField,
  Box,
  Paper,
  useTheme
} from '@mui/material';

// ====== Importing images/logos ======
import FleetGptIcon from '../assets/icons/AI.svg';
import TopDriverIcon from '../assets/icons/totalDriver.svg';
import MostDrivenIcon from '../assets/icons/Vehical.svg';
import LocationIcon from '../assets/icons/Location.svg';
import VehicleIcon from '../assets/icons/Vehical.svg';
import TripsIcon from '../assets/icons/map.svg';
import ExpandIcon from '../assets/icons/Expand.svg';
import CloseIconImage from '../assets/icons/Close Square.svg';
import HomeIcon from '../assets/icons/home.svg';
import ChatIcon from '../assets/icons/chat.svg';
import ArrowUP from '../assets/icons/arrow-up-right_Purple.svg';
import SendIcon from '../assets/icons/Send.svg';
import { AuthContext } from "../authContext/authContext";

// ====== Import chat layout ======
import FleetgptChatLayout from "./FleetgptChatLayout";
import { useAuth } from "../authContext/useAuth";

interface FleetgptLayoutProps {
  onClose: () => void;
  chatInfo?: {
    user_id: string;
    conversation_id: string;
    message: string;
  };
}

export default function FleetgptLayout({ onClose, chatInfo }: FleetgptLayoutProps) {
  const theme = useTheme();
  const [selectedTab, setSelectedTab] = useState<'Home' | 'Chat History'>('Home');
  const [mo, setMo] = useState(600);
  // If chatInfo is provided, initialize message with it
  const [message, setMessage] = useState(chatInfo?.message || '');
  const [selectedPrompt, setSelectedPrompt] = useState<null | { description: string }>(null);
  const authContext = useContext(AuthContext);
  const showChat = authContext?.showChat ?? false;
  const setShowChat = authContext?.setShowChat ?? (() => { });
  const { user } = useAuth();
  const [questionId, setQuestionId] = useState<number | null>(null);

  console.log ('chatInfo:', chatInfo);

  const promptTemplates = [
    { id: 1, label: 'Total Vehicles', description: 'View total vehicles across fleets.', image: VehicleIcon },
    { id: 6, label: 'Most Driven Vehicle', description: 'Which vehicle driven the most?', image: LocationIcon },
  ];

  useEffect(() => {
    // This useEffect for 'mo' will no longer be necessary for the Paper height adjustment
    // if you switch to minHeight, but it can stay if 'mo' is used elsewhere.
    const adjustModalHeight = () => {
      const windowHeight = window.innerHeight;
      setMo(windowHeight - 70 < 600 ? windowHeight - 70 : 600);
    };
    window.addEventListener('resize', adjustModalHeight);
    adjustModalHeight();
    return () => window.removeEventListener('resize', adjustModalHeight);
  }, []);

  return (
    <>
      {!showChat && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            bgcolor: theme.palette.background.default
          }}
        >
          <Paper
            elevation={3}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              maxWidth: 384,
              bgcolor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '9.6px',
              boxShadow: theme.shadows[4],
              // Remove fixed height and use minHeight or no height at all to fit content
              // height: mo, // Remove this line
              minHeight: '400px', // Example: Set a reasonable minimum height
              overflow: 'hidden', // Keep overflow hidden for the content inside
              // Add a flex-shrink to prevent it from growing unnecessarily if a parent has flex-grow
              flexShrink: 0
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: 55,
                bgcolor: theme.palette.background.paper,
                px: 1
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <img
                  src={FleetGptIcon}
                  alt="Fleet GPT Icon"
                  width={19}
                  height={19}
                  style={{
                    marginRight: theme.spacing(1),
                    filter: theme.palette.mode === 'dark' ? 'brightness(0) invert(1)' : 'none'
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '19px', lineHeight: '29px' }}>
                  Fleet GPT
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* <IconButton>
                  <img src={ExpandIcon} alt="Expand Icon" width={19} height={19} />
                </IconButton> */}
                <IconButton onClick={onClose}>
                  <img src={CloseIconImage} alt="Close Icon" width={19} height={19} />
                </IconButton>
              </Box>
            </Box>

            {/* Body */}
            {/* Make the body flexible to push the footer to the bottom of its content */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  fontSize: '13px',
                  lineHeight: '19px',
                  color: theme.palette.text.secondary,
                  textAlign: 'center',
                  mb: 2
                }}
              >
                How can I help you today?
              </Typography>

              {/* Prompt Templates */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '14px', lineHeight: '22px' }}>
                    Prompt Templates
                  </Typography>
                </Box>
                <Box sx={{ maxHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {promptTemplates.map((item, id) => (
                    <Box
                      key={id}
                      onClick={() => {
                        setSelectedPrompt({ description: item.description });
                        setQuestionId(item.id);
                        setShowChat(true);
                      }}
                      sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        p: 1,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '6px'
                      }}
                    >
                      <Box sx={{
                        width: 26,
                        height: 26,
                        borderRadius: '6px',
                        mr: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor:
                          theme.palette.mode === 'dark' &&
                            ['Most Driven Vehicle', 'Vehicle Last Location', 'Trip Details'].includes(item.label)
                            ? '#fff'
                            : theme.palette.action.hover
                      }}>
                        <img src={item.image} alt={item.label} width={20} height={20} />
                      </Box>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '11px', lineHeight: '17px' }}>
                          {item.label}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '11px', lineHeight: '17px', color: theme.palette.text.secondary }}>
                          {item.description}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Push the footer to the bottom if content is short */}
              <Box sx={{ flexGrow: 1 }} />

              {/* Footer Tabs */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-around',
                  alignItems: 'center',
                  bgcolor: '#EEEDFC',
                  height: 67,
                  borderRadius: '9.6px',
                  p: 1,
                  // Ensure this box doesn't have extra margin/padding below it from parent
                }}
              >
                {(['Home', 'Chat History'] as const).map((tab) => (
                  <Button
                    key={tab}
                    onClick={() => {
                      setSelectedTab(tab);
                      if (tab === 'Chat History') {
                        if (questionId === null) {
                          setQuestionId(1); // Set a default questionId
                        }
                        setShowChat(true); // Open FleetgptChatLayout when "Chat History" is clicked
                      }
                    }}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      width: 171,
                      height: 55,
                      borderRadius: '6px',
                      bgcolor: selectedTab === tab
                        ? theme.palette.mode === 'dark' ? '#555555' : '#fff'
                        : 'transparent'
                    }}
                  >
                    <img
                      src={tab === 'Home' ? HomeIcon : ChatIcon}
                      alt={tab}
                      width={15}
                      height={15}
                      style={{ filter: theme.palette.mode === 'dark' ? 'brightness(0) invert(1)' : 'none' }}
                    />
                    <Typography
                      sx={{
                        fontSize: '10px',
                        color: selectedTab === tab
                          ? theme.palette.text.primary
                          : theme.palette.mode === 'dark'
                            ? theme.palette.text.secondary
                            : '#000'
                      }}
                    >
                      {tab}
                    </Typography>
                  </Button>
                ))}
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Chat Layout Overlay */}
      {showChat && questionId !== null && (
        <FleetgptChatLayout
          initialMessage={selectedPrompt ? selectedPrompt.description : message}
          questionId={questionId}
          customer_id={user?.customer_id}
          vehicleId=""
          chatInfo={chatInfo}
          onClose={() => {
            setShowChat(false);
            setMessage('');
            setSelectedTab('Home');
            setSelectedPrompt(null);
            if (typeof onClose === 'function') {
              onClose();
            }
          }}
          onBack={() => {
            setShowChat(false);
            setMessage('');
            setSelectedTab('Home');
            setSelectedPrompt(null);
          }}
        />
      )}
    </>
  );
}